/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as CompanyLogo from '@/Components/Global/CompanyLogo/template.vue'
import * as SubmitButton from '@/Components/Global/SubmitButton/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppValues } from '@/Constants/AppValues'
import { Component } from '@/Constants/Component'
import { Server } from '@/Constants/Server'
import { VueRouter } from '@/Constants/VueRouter'

/**************************************************************************************************/
/** @import Enums                                                                                **/
/**************************************************************************************************/
import { LoginTypes } from '@/Enums/LoginTypes'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch from '@/Mixins/MixinFetch'
import MixinResponsive from '@/Mixins/MixinResponsive'
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @internal Const @name LoginForm                                                              **/
/**************************************************************************************************/
const LoginForm = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'LoginForm', mixins: [MixinComponent, MixinFetch, MixinResponsive],
	
	components:
	{
		CompanyLogo: CompanyLogo.default,
		SubmitButton: SubmitButton.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			inputs:
			{
				email: '' as string | null,
				password: ''
			},
			states:
			{
				action: Component.Actions.INSERT,
				isFetching: false,
				type: LoginTypes.User,
				rememberMe: false,
				visibility: false
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	mounted() : void
	{
		// Verificar si existen datos guardados en 'localStorage'.
		if (this.CheckCredentials())
		{
			const { email } = this.LoadCredentials()
			this.inputs.email = email || null
			this.states.rememberMe = true
		}
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		EmailInputState() : boolean | null
		{
			const { email } = this.inputs
			if (email === '') return null
			return this.IsValidEmailFormat(email)
		},

		GetEmailPlaceHolder() : string
		{
			return this.IsTypeUser ? 'tucorreo@ejemplo.cl' : ''
		},

		GetInputPasswordType() : string
		{
			if (this.IsTypeStorage) return 'text'
			return this.states.visibility ? 'text' : 'password'
		},

		IsTypeStorage() : boolean
		{
			return this.states.type === LoginTypes.Storage
		},

		IsTypeUser() : boolean
		{
			return this.states.type === LoginTypes.User
		},

		PasswordInputState() : boolean | null
		{
			if (this.inputs.password?.length === 0) return null
			return this.inputs.password?.length > 0
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		CheckCredentials() : boolean
		{
			const { email, password } = this.LoadCredentials()
			return email && password ? true : false
		},

		GetSubmitButtonText() : string
		{
			const { isFetching } = this.states
			return isFetching ? 'Iniciando Sesión' : 'Ingresar'
		},

		GetText(key: string) : string
		{
			const isUser = this.IsTypeUser
			switch (key)
			{
				case 'email': return isUser ? 'Correo Electrónico' : 'Código Planta'
				case 'password': return isUser ? 'Contraseña' : 'Código Operador'
				default: return ''
			}
		},

		IsValidEmailFormat(email: string | null) : boolean
		{
			if (!email) return false
			if (!this.IsTypeUser) return true
			return /^[a-z0-9]+(.+)@[a-z0-9]+\.[a-z0-9]+$/i.test(email)
		},

		LoadCredentials() : { email: string | null, password: string | null }
		{
			const email = localStorage.getItem(`${ AppValues.Strings.PREFIX_LOCAL_STORAGE }userEmail`)
			const password = localStorage.getItem(`${ AppValues.Strings.PREFIX_LOCAL_STORAGE }userPass`)
			return { email, password }
		},

		OnInputChange() : void
		{
			this.inputs.email = this.inputs.email?.toLowerCase() || null
		},

		OnInputPasswordKeyUp() : void
		{
			if (this.IsTypeUser) return
			this.inputs.password = TypesHelper.toStringValidRutFormat(this.inputs.password)!
		},

		OnLFResetPassword() : void
		{
			this.$router.push({ name: VueRouter.Views.Global.PASSWORD_RESET.NAME })
		},

		OnServerCaughtFetchException() : void
		{
			this.Set<LoginFormRef['states']>({ isFetching: false })
			this.$emit('onFailedLogin')
		},

		OnServerFailedResponse() : void
		{
			this.Set<LoginFormRef['states']>({ isFetching: false })
			this.$emit('onFailedLogin')
		},

		async OnSubmitClick() : Promise<void>
		{
			this.Set<LoginFormRef['states']>({ isFetching: true })
			const { email, password } = this.inputs

			if (this.IsValidEmailFormat(email) && password !== '')
			{
				// Emitir evento cuando el Correo sea valido y exista una clave ingresada.
				this.$emit('onLFButtonClick')

				// Generar la petición para validar a nivel de Servidor los datos ingresados.
				const accessToken = Buffer.from(`${ email }:${ password }`, 'utf8').toString('base64')

				// Cabezera de la Petición
				const headers = { 'Authorization': `Basic ${ accessToken }` }

				// Realizar la Petición para autenticar al usuario.
				const endpoint = this.IsTypeUser ? Server.Routes.Commons.Login : Server.Routes.Commons.LoginStorage
				const response = await this.Fetch(Server.Fetching.Method.POST, endpoint, null, headers)

				// Si se obtiene una respuesta satisfactoria, continuar con el proceso.
				if (response?.status === Server.Response.StatusCodes.SUCCESS)
				{
					// Si el CheckBox esta seleccionado, los datos serán guardados, localmente, de lo contrario, serán borrados.
					this.SaveCredentials(email!, password)

					// Emitir el Evento de Inicio de Sesión exitoso.
					const accessToken = (response.data as any).access_token
					this.$emit('onSuccessLogin', { accessToken }, password)
				}
			}
			else
			{
				this.Set<LoginFormRef['states']>({ isFetching: false })
				this.$emit('onFailedLogin')
			}
		},

		SaveCredentials(email: string, password: string) : void
		{
			const rMe = this.states.rememberMe
			localStorage.setItem(`${ AppValues.Strings.PREFIX_LOCAL_STORAGE }userEmail`, rMe ? email : '')
			localStorage.setItem(`${ AppValues.Strings.PREFIX_LOCAL_STORAGE }userPass`, rMe ? password : '')
		},

		UpdateType(type: LoginTypes) : void
		{
			this.inputs.email = ''
			this.inputs.password = ''
			this.Set<LoginFormRef['states']>({ type })
		}
	}
})

/**************************************************************************************************/
/** @export Component @name LoginForm                                                            **/
/**************************************************************************************************/
export default LoginForm

/**************************************************************************************************/
/** @export Type @name LoginFormRef                                                              **/
/**************************************************************************************************/
export type LoginFormRef = InstanceType<typeof LoginForm>